import { useEffect, FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import clsx from 'clsx'

import { setLoggedIn } from 'store/actions/auth'
import ErrorBoundary from 'components/ErrorBoundary'
import ForgotCompany from 'NoAuth/ForgotCompany'
import ForgotPassword from 'NoAuth/ForgotPassword'
import ForgotUsername from 'NoAuth/ForgotUsername'
import HandleTransfer from 'NoAuth/HandleTransfer'
import Login from 'NoAuth/Login'
import PunchLocation from 'NoAuth/PunchLocation'
import Reset from 'NoAuth/Reset'
import SelectCompany from 'NoAuth/SelectCompany'
import { hasAccessToThis } from 'services/Utilities'

import useRedirectTo from 'components/Hooks/useRedirectTo'
import LanguageDropDown from 'components/UI/LanguageDropDown'

import styles from './noAuth.module.scss'

export const noAuthPaths = ['/company', '/forgot', '/login', '/punchLocation', '/reset', '/transfer']

const NoAuth: FC = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { isAuthenticated, co } = useSelector((state: MStore.RootState) => state.auth)
  const redirectTo = useRedirectTo()

  // Log in the user if they have a token but are not logged in
  useEffect(() => {
    if (!isAuthenticated) {
      const token = localStorage.getItem('token')
      if (token) dispatch(setLoggedIn(token))
    }
  }, [isAuthenticated, dispatch])

  // If no company was found, redirect the user to a page to select one
  if (
    (!co || (co === 'localhost' && Capacitor.isNativePlatform())) &&
    pathname !== '/company' &&
    pathname !== '/forgot/company' &&
    !pathname.includes('/transfer')
  )
    return <Redirect to="/company" />

  const myDefaultPath = hasAccessToThis('managerPanel') ? '/manage/dashboard' : '/my/home'

  return (
    <div className="d-flex" style={{ height: '100vh' }}>
      {pathname === '/login' ? (
        <div className="d-none d-lg-flex flex-even p-5">
          <img src="https://cloud.merinio.com/login_image_m2.png" className={clsx('my-auto', 'mx-auto', 'w-100', styles['login-image'])} alt="login" />
        </div>
      ) : null}
      <div className="bg-primary p-3 flex-even d-flex">
        <div className="mx-auto my-auto col-md-10 d-flex flex-column  pb-5">
          <img src="https://cloud.merinio.com/merinio_long_logo_white.svg" alt="Merinio logo" className={clsx(styles['login-logo'], 'mb-2')} />
          <LanguageDropDown />
          <ErrorBoundary>
            <Switch>
              {isAuthenticated ? <Redirect from="/login" to={redirectTo ?? myDefaultPath} /> : null}
              {/* Forgot section */}
              <Route exact path="/forgot/company" component={ForgotCompany} />
              <Route exact path="/forgot/password" component={ForgotPassword} />
              <Route exact path="/forgot/username" component={ForgotUsername} />
              {/* Others */}
              <Route exact path="/login" component={Login} />
              <Route exact path="/company" component={SelectCompany} />
              <Route exact path="/punchLocation" component={PunchLocation} />
              <Route exact path="/reset/:token" component={Reset} />
              <Route exact path="/transfer/:token" component={HandleTransfer} />
            </Switch>
          </ErrorBoundary>
          <img
            src="https://cloud.merinio.com/login_image_m2.png"
            className={clsx('d-block', 'd-lg-none', styles['login-image-small'], 'mx-auto', 'mt-2')}
            alt="login-small"
          />
        </div>
      </div>
    </div>
  )
}

export default NoAuth
